import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as LeftArrow } from '../../../../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../../../../assets/icons/right-arrow.svg';
import DefaultApiLimit from '../../../../../config/defaultLimit';
import {
  RequestUpcomingMaintenanceInterFace,
  UpcomingMaintenanceInterFace,
} from '../../../../../interface/upcoming-maintenance-location-interface';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import {
  disableUpcomingMaintenanceFiltersApplied,
  fetchUpcomingMaintenance,
  fetchListUpcomingMaintenanceFilters,
  updateUpcomingMaintenanceFilters,
  setSortData,
} from '../../../../../redux/slices/UpcomingMaintenanceSlice';
import CustomDateRangePicker from '../../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import { GetParametersFromCommonFilters } from '../../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../../shared-functions/ConvertUTCtoHHMM';
import { changeDateFormat } from '../../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../../shared-functions/ExportFile';
import FormatUtcString from '../../../../../shared-functions/FormatUtcString';
import stationManagementApi from '../../../../../utils/api/location-management';
// import { fetchLocationActivityFilters } from '../../../../redux/slice/LocationActivitySlice';

interface MaintenanceScheduleReportProperties {
  day: number;
  month: number;
  year: number;
  status: string;
}

interface SchedulesInterface {
  totalHoursSchedule: number;
  totalCount: number;
  totalAvailableHours: string;
}

/**
 * Creating circle size animation based on its value
 * @param {number} value The value to scale
 * @param {number} minSize The minimum size
 * @param {number} maxSize The maximum size
 * @returns {number} The calculated size
 */
const getCircleSize = (value: number, minSize: number = 30, maxSize: number = 120) => {
  // Scale the value proportionally between min and max size
  const size = minSize + value;
  return Math.min(size, maxSize);
};

/**
 * Up coming maintenance schedule component
 * @returns {any} The rendered component
 */
function UpcomingMaintenanceSchedule() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<any[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    UpcomingMaintenance,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.upcomingMaintenances);
  const [maintenanceReport, setMaintenanceReport] = useState<MaintenanceScheduleReportProperties[]>(
    [],
  );
  const { selectedChargingPoint } = useAppSelector((state) => state.locationManagement);

  const months = useMemo(
    () => [
      t('january'),
      t('february'),
      t('march'),
      t('april'),
      t('may'),
      t('june'),
      t('july'),
      t('august'),
      t('september'),
      t('october'),
      t('november'),
      t('december'),
    ],
    [t],
  );
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [schedules, setSchedules] = useState<SchedulesInterface>({
    totalHoursSchedule: 0,
    totalCount: 0,
    totalAvailableHours: '',
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
        />
      </div>
    );
  };

  /**
   * Handle the fetch the data in api function
   * @param {RequestUpcomingMaintenanceInterFace} data The request data
   */
  const fetchData = (data: RequestUpcomingMaintenanceInterFace) => {
    if (!loading) {
      dispatch(fetchUpcomingMaintenance({ ...data, chargingStation: selectedChargingPoint?.id }));
    }
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: UpcomingMaintenance,
    });
  const column = useMemo(
    () => [
      {
        label: t('maintenanceDate'),
        accessorKey: 'startDateUtc' as keyof UpcomingMaintenanceInterFace,
        sortAllow: true,
        /**
         * Converting timestamp to UTC date
         * @param {any} maintenance The maintenance object
         * @returns {any} The date element
         */
        customData: (maintenance: UpcomingMaintenanceInterFace) => {
          if (!maintenance.endDateUtc) {
            return <p>{ConvertUTCtoDate(maintenance.startDateUtc)}</p>;
          }
          return (
            <p>
              {ConvertUTCtoDate(maintenance.startDateUtc)}-
              {ConvertUTCtoDate(maintenance.endDateUtc)}
            </p>
          );
        },
      },
      {
        label: t('maintenanceTime'),
        accessorKey: 'maintenanceTime' as keyof UpcomingMaintenanceInterFace,
        sortAllow: false,
        /**
         * Converting timestamp to UTC time
         * @param {any} maintenanceTime The maintenance object
         * @returns {any} The time element
         */
        customData: (maintenanceTime: UpcomingMaintenanceInterFace) => {
          if (!maintenanceTime.endDateUtc) {
            return <p>{FormatUtcString(ConvertUTCtoHHMM(maintenanceTime.startDateUtc))}</p>;
          }
          return (
            <p>
              {FormatUtcString(ConvertUTCtoHHMM(maintenanceTime.startDateUtc))}-
              {FormatUtcString(ConvertUTCtoHHMM(maintenanceTime.endDateUtc))}
            </p>
          );
        },
      },
      {
        label: t('chargingPointName'),
        accessorKey: 'chargingPointName' as keyof UpcomingMaintenanceInterFace,
        sortAllow: false,
        queryParamsKey: 'deviceId',
        queryParamsValue: 'chargingPointSn',
        sidePanelComponent: (
          <DeviceSidePanel
            fetchIdFrom="queryParams"
            isEditMode={isEditMode}
            onEditModeChange={handleEditModeChange}
          />
        ),
        disableNewTab: true,
      },
      {
        label: t('task'),
        accessorKey: 'type' as keyof UpcomingMaintenanceInterFace,
        sortAllow: false,

        /**
         * Converting timestamp to UTC time
         * @param {any} maintenanceTime The maintenance object
         * @returns {any} The task element
         */
        customData: (maintenanceTime: UpcomingMaintenanceInterFace) => (
          <p>
            {maintenanceTime.type
              ? maintenanceTime.type.charAt(0).toUpperCase() + maintenanceTime.type.slice(1)
              : '-'}
          </p>
        ),
      },
      {
        label: t('duration'),
        accessorKey: 'duration' as keyof UpcomingMaintenanceInterFace,
        sortAllow: true,
      },
      {
        label: t('status'),
        accessorKey: 'status' as keyof UpcomingMaintenanceInterFace,
        sortAllow: false,
      },
    ],
    [t],
  );

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('maintenanceDate'), accessorKey: 'startDateUtc' },
    { label: t('maintenanceTime'), accessorKey: 'maintenanceTime' },
    { label: t('chargingPointName'), accessorKey: 'chargingPointName' },
    { label: t('task'), accessorKey: 'type' },
    { label: t('duration'), accessorKey: 'duration' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    stationManagementApi
      .fetchMaintenanceScheduleExport({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        chargingStation: selectedChargingPoint?.id,
      })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: UpcomingMaintenanceInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'startDateUtc':
                return (
                  `${ConvertUTCtoDate(row?.startDateUtc)} - ${ConvertUTCtoDate(row?.endDateUtc)}` ||
                  'N/A'
                );
              case 'maintenanceTime':
                return (
                  `${ConvertUTCtoHHMM(row?.startDateUtc)}-${ConvertUTCtoHHMM(row?.endDateUtc)}` ||
                  'N/A'
                );
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, 'charging-location-approved.csv');
        } else {
          exportPDF(headers, rows, 'charging-location-approved.pdf');
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Get the all days use month function
   * @param {number} month The month
   * @param {number} year The year
   */
  const getAllDays: (month: number, year: number) => void = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    let days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    days = days.map((valueChange) => ({
      date: changeDateFormat(valueChange.toString()),
      number: valueChange.getDate(),
      day: valueChange.getDay(),
    }));
    if (days[0].day > 0) {
      const firstMonth = days[0].day;
      for (let index = 0; index < firstMonth; index += 1) {
        days = [{}, ...days];
      }
    }
    setAllDates(days);
  };
  /**
   * Handle the month change function
   * @param {string} type The change type ('add' or 'sub')
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);

      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Function to fetch maintenance schedule report
   * @param {any} data The data to fetch
   */
  const handleFetchMaintenanceReport = async (data: any) => {
    try {
      const response = await stationManagementApi.fetchMaintenanceCalendars({
        ...data,
        chargingStation: selectedChargingPoint?.id,
      });
      setMaintenanceReport(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to return a maintenance
   * @param {string} date The date string
   * @returns {string} The maintenance status
   */
  const handleShowMaintenanceSchedule = (date: string): string => {
    const [year, month, day] = date.split('-');

    const numericDay = Number(day); // 1
    const numericMonth = Number(month); // 10

    const maintenance = maintenanceReport.find(
      (eachReport) =>
        eachReport?.day?.toString() === numericDay.toString() &&
        eachReport?.month?.toString() === numericMonth.toString() &&
        eachReport?.year?.toString() === year,
    );

    if (!maintenance) return 'no-schedule';

    return maintenance?.status;
  };

  /**
   * Table pagination function
   * @param {any} value The pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };
  /**
   * Function for sorting table data
   * @param {object} sortData The sort configuration
   * @param {any} sortData.sortBy - The sortBy prop
   * @param {any} sortData.sortField - The sortField prop
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle redirect device management page with filter
   * @param {string} key The status key
   */
  const handleClickCircle = (key: string) => {
    dispatch(
      updateUpcomingMaintenanceFilters({
        key: 'deviceStatus',
        value: key,
        type: 'restAndAdd',
      }),
    );
  };

  /**
   * Handle fetch no of schedules
   * @param {any} payload payload The payload
   * @param {any} payload.startDate - The startDate prop
   * @param {any} payload.endDate - The endDate prop
   */
  const fetchSchedule = async (payload: { startDate?: string | null; endDate?: string | null }) => {
    if (!payload.startDate || !payload.endDate) return;

    try {
      const response = await stationManagementApi.fetchNoOfSchedules({
        chargingStation: selectedChargingPoint?.id,
        selectDate: payload.startDate ?? null,
        endDate: payload.endDate ?? null,
      });

      setSchedules(response.data);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  /**
   * Handling date click
   * @param {string} selectedDate The selected date
   */
  const handleDateClick = (selectedDate: string) => {
    let newRange = { ...dateRange };

    if (!newRange.startDate || (newRange.startDate && newRange.endDate)) {
      // First click or reset
      newRange = { startDate: selectedDate, endDate: '' };
    } else {
      // Second click
      const start = new Date(newRange.startDate);
      const end = new Date(selectedDate);

      if (end < start) {
        newRange = { startDate: selectedDate, endDate: newRange.startDate };
      } else {
        newRange = { ...newRange, endDate: selectedDate };
      }
    }

    // ✅ update state
    setDateRange(newRange);

    // ✅ call API ONLY when both dates exist
    if (newRange.startDate && newRange.endDate) {
      fetchSchedule({
        startDate: newRange.startDate,
        endDate: newRange.endDate,
      });
    }
  };

  useEffect((): void => {
    getAllDays(currentMonth, currentYear);
  }, []);

  const previousQueryKey = useRef<string | null>(null);

  useEffect(() => {
    const stationId = selectedChargingPoint?.id;
    const queryKey = `${stationId}|${dateRange.startDate}|${dateRange.endDate}`;
    const isQueryKeyChanged = previousQueryKey.current !== queryKey;
    previousQueryKey.current = queryKey;

    // Fetch only when the station/date range changed or the user applied/cleared a filter.
    // The filter list thunk also replaces `filters`, which must not re-trigger the list API.
    if (stationId && (isFilterUpdated || isQueryKeyChanged)) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
      });
      dispatch(disableUpcomingMaintenanceFiltersApplied());
    }
  }, [filters, dateRange.startDate, dateRange.endDate, selectedChargingPoint?.id]);

  useEffect(() => {
    if (selectedChargingPoint) {
      handleFetchMaintenanceReport({});
      if (dateRange.startDate && dateRange.endDate) {
        fetchSchedule({ startDate: dateRange.startDate, endDate: dateRange.endDate });
      }
    }
  }, [selectedChargingPoint?.id]);

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('UpcomingMaintenanceFilters'); // string | null

    // Load the filter list only for a new station or when it is not loaded yet.
    // Must not depend on `filters`, otherwise an empty list would re-request forever.
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchListUpcomingMaintenanceFilters({ chargingStation: currentId }));
    }

    // Save this id so next time we can compare
    sessionStorage.setItem('UpcomingMaintenanceFilters', currentId);
  }, [selectedChargingPoint?.id]);

  return (
    <div className="upcoming-maintenance-schedules-wrap" dir={i18n.dir()}>
      <div className="upcoming-maintenance-schedules-table">
        <CustomTable
          version="v2"
          header={column}
          data={UpcomingMaintenance}
          serialNo
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filtersToDispatch={updateUpcomingMaintenanceFilters}
          filterListToDispatch={fetchListUpcomingMaintenanceFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refershCurrentPages={handleRefresh}
          pageLimitChange={pageNateLimitChange}
          handleSortChange={handleSortFunction}
          sortData={{ sortBy, sortField }}
          onHiddenColumnsChange={setHiddenColumns}
          height={400}
          rightTopContent={renderDateFilters()}
          onClearFilterChange={() => {
            clearDateRange();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
        />
      </div>
      {selectedChargingPoint?.id === 'love' && (
        <div className="upcoming-maintenance-chart">
          <div className="upcoming-maintenance-calendar-wrap">
            <div className="calender-header">
              <div
                className="calender-header-left"
                onClick={() => changeMonth('sub')}
                role="presentation"
                style={{
                  transform: i18n.dir() === 'rtl' ? 'rotate(0deg)' : 'rotate(180deg)',
                }}>
                <LeftArrow />
              </div>
              <div className="calender-header-center">
                {months[currentMonth]} - {currentYear}
              </div>
              <div
                className="calender-header-right"
                onClick={() => changeMonth('add')}
                role="presentation"
                style={{
                  transform: i18n.dir() === 'rtl' ? 'rotate(180deg)' : 'rotate(0deg)',
                }}>
                <RightArrow />
              </div>
            </div>
            <div className="calender-body">
              <div className="calender-header-items">
                <div className="header-item">{t('sun')}</div>
                <div className="header-item">{t('mon')}</div>
                <div className="header-item">{t('tue')}</div>
                <div className="header-item">{t('wed')}</div>
                <div className="header-item">{t('thu')}</div>
                <div className="header-item">{t('fri')}</div>
                <div className="header-item">{t('sat')}</div>
              </div>
              <div className="calender-body-items">
                {allDates.map((dateDetail: any) =>
                  Object.keys(dateDetail).length === 0 ? (
                    <div className="header-item" />
                  ) : (
                    <div
                      className="header-item hover-date"
                      onClick={() => handleDateClick(dateDetail.date)}
                      role="presentation">
                      <p
                        className={`${handleShowMaintenanceSchedule(dateDetail?.date || '')} ${
                          dateRange.startDate === dateDetail.date ||
                          dateRange.endDate === dateDetail.date ||
                          (dateRange.startDate &&
                            dateRange.endDate &&
                            dateDetail.date >= dateRange.startDate &&
                            dateDetail.date <= dateRange.endDate)
                            ? 'active-date'
                            : ''
                        }`}>
                        {dateDetail.number}
                      </p>
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
          <div className="dashboard-device-status-container">
            <div className="device-status-top">
              <div
                className="outer-circle go-green"
                role="presentation"
                onClick={() => handleClickCircle('online')}
                style={{
                  width: `${getCircleSize(schedules.totalCount)}px`,
                  height: `${getCircleSize(schedules.totalCount)}px`,
                }}>
                <div className="inner-circle">{schedules.totalCount}</div>
              </div>
              <div
                className="outer-circle need-attention"
                role="presentation"
                onClick={() => handleClickCircle('offline')}
                style={{
                  width: `${getCircleSize(Number(schedules.totalAvailableHours))}px`,
                  height: `${getCircleSize(Number(schedules.totalAvailableHours))}px`,
                }}>
                <div className="inner-circle">{schedules.totalAvailableHours}</div>
              </div>
              <div
                className="outer-circle out-of-service"
                role="presentation"
                onClick={() => handleClickCircle('offline')}
                style={{
                  width: `${getCircleSize(schedules.totalHoursSchedule)}px`,
                  height: `${getCircleSize(schedules.totalHoursSchedule)}px`,
                }}>
                <div className="inner-circle">{schedules.totalHoursSchedule}</div>
              </div>
              <div className="absolute-container">
                <svg
                  width="213"
                  height="28"
                  viewBox="0 0 213 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M1.22266 15.9421C9.76059 10.3572 33.8465 -0.315986 61.8869 1.66973C96.9373 4.15187 198.044 37.0397 212.199 23.3879"
                    stroke="url(#paint0_linear_49_10663)"
                    strokeWidth={1.26333}
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_49_10663"
                      x1="1.22266"
                      y1="14.0601"
                      x2="212.199"
                      y2="14.0601"
                      gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00A79D" />
                      <stop offset="1" stopColor="#6EC282" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Legend on right side */}
            <div className="legend">
              <div className="legend-item">
                <span className="dot green" />
                <p>{t('totalNoOfSchedules')}</p>
              </div>
              <div className="legend-item">
                <span className="dot violet" />
                <p>{t('noOfHoursAvailableForCommercial')}</p>
              </div>
              <div className="legend-item">
                <span className="dot orange" />
                <p>{t('noOfHoursSchedule')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UpcomingMaintenanceSchedule;
